import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ReactComponent as DotsIcon } from '../../icons/dots.svg';
import './ActionMenu.css';

function ActionMenu({ items, align, ariaLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ vertical: 'down', horizontal: align });
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      const target = event.target;
      if (menuRef.current?.contains(target)) {
        return;
      }
      if (listRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let frameId = 0;

    const updatePosition = () => {
      const buttonEl = buttonRef.current;
      const listEl = listRef.current;
      if (!buttonEl || !listEl) {
        return;
      }

      const buttonRect = buttonEl.getBoundingClientRect();
      const listRect = listEl.getBoundingClientRect();
      const viewportPadding = 8;
      const gap = 8;
      const availableHeight = window.innerHeight - viewportPadding * 2;

      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const listHeight = listRect.height || listEl.scrollHeight;

      let vertical = 'down';
      if (spaceBelow < listHeight + gap + viewportPadding && spaceAbove > spaceBelow) {
        vertical = 'up';
      }

      const spaceRight = window.innerWidth - buttonRect.right;
      const spaceLeft = buttonRect.left;
      let horizontal = align;
      if (horizontal === 'right' && spaceRight < listRect.width + viewportPadding && spaceLeft >= listRect.width + viewportPadding) {
        horizontal = 'left';
      } else if (horizontal === 'left' && spaceLeft < listRect.width + viewportPadding && spaceRight >= listRect.width + viewportPadding) {
        horizontal = 'right';
      }

      let top = vertical === 'down'
        ? buttonRect.bottom + gap
        : buttonRect.top - listHeight - gap;

      const left = horizontal === 'right'
        ? buttonRect.right - listRect.width
        : buttonRect.left;

      const needsScroll = listHeight > availableHeight;
      const effectiveHeight = needsScroll ? availableHeight : listHeight;

      if (vertical === 'down' && top + effectiveHeight > window.innerHeight - viewportPadding) {
        top = Math.max(viewportPadding, window.innerHeight - viewportPadding - effectiveHeight);
      }
      if (vertical === 'up' && top < viewportPadding) {
        top = viewportPadding;
      }

      const clampedTop = Math.max(viewportPadding, Math.min(top, window.innerHeight - effectiveHeight - viewportPadding));
      const clampedLeft = Math.max(viewportPadding, Math.min(left, window.innerWidth - listRect.width - viewportPadding));

      setPosition({ vertical, horizontal });
      setMenuStyle({
        top: `${clampedTop}px`,
        left: `${clampedLeft}px`,
        maxHeight: needsScroll ? `${availableHeight}px` : undefined,
        overflowY: needsScroll ? 'auto' : undefined,
      });
    };

    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        updatePosition();
        frameId = window.requestAnimationFrame(updatePosition);
      });
    };

    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
    };
  }, [isOpen, align, items]);

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const handleItemClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    if (item.disabled) {
      return;
    }
    item.onClick?.();
    setIsOpen(false);
  };

  const menuList = isOpen ? (
    <div
      ref={listRef}
      className={`action-menu-list action-menu-list-${position.horizontal} action-menu-list-${position.vertical}`}
      style={menuStyle}
      role="menu"
    >
      {items.map((item) => {
        const ItemIcon = item.icon;
        return (
          <button
            key={item.key || item.label}
            type="button"
            role="menuitem"
            className={`action-menu-item${item.danger ? ' danger' : ''}${item.disabled ? ' disabled' : ''}`}
            onClick={(event) => handleItemClick(event, item)}
            disabled={item.disabled}
          >
            {ItemIcon && <ItemIcon />}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <div className="action-menu" ref={menuRef}>
      <button
        type="button"
        className={`action-menu-button${isOpen ? ' action-menu-button-active' : ''}`}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={handleToggle}
        ref={buttonRef}
      >
        <DotsIcon />
      </button>
      {menuList && createPortal(menuList, document.body)}
    </div>
  );
}

ActionMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      onClick: PropTypes.func,
      danger: PropTypes.bool,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  align: PropTypes.oneOf(['left', 'right']),
  ariaLabel: PropTypes.string,
};

ActionMenu.defaultProps = {
  align: 'right',
  ariaLabel: 'Otwórz menu akcji',
};

export default ActionMenu;
